import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
    faBath,
    faBorderAll,
    faCartXmark,
    faDogLeashed,
    faHandWave,
    faHotel,
    faListCheck,
    faTennisBall,
    faVanShuttle,
    faWhistle
} from '@fortawesome/pro-regular-svg-icons';
import React from 'react';
import { NavLink } from 'react-router-dom';
import { readDaycareBookingsView } from 'components/Bookings2/Daycare/Kanban/daycareViewStorage';
import {
    BookingsAbandonedPath,
    BookingsAllPath,
    BookingsBoardingPath,
    BookingsDaycarePath,
    BookingsEvaluationPath,
    BookingsGroomingPath,
    BookingsKanbanPath,
    BookingsRequestedPath,
    BookingsTransportationPath,
    BookingsTrainingPath,
    BookingsWalkingPath
} from 'routes/Paths';

export type BookingTabId =
    | 'daycare'
    | 'boarding'
    | 'grooming'
    | 'training'
    | 'transportation'
    | 'walking'
    | 'evaluation'
    | 'abandoned'
    | 'requested'
    | 'all';

export interface BookingTab {
    id: BookingTabId;
    label: string;
    path: string;
    icon: IconDefinition;
    visible: boolean;
    isActive?: React.ComponentProps<typeof NavLink>['isActive'];
}

export interface BookingTabVisibility {
    hasGrooming: boolean;
    hasMobileGrooming: boolean;
    hasDental: boolean;
    hasDaycare: boolean;
    hasBoarding: boolean;
    hasEvaluation: boolean;
    hasTransportation: boolean;
    hasTraining: boolean;
    hasWalking: boolean;
}

export const getBookingTabs = ({
    hasGrooming,
    hasMobileGrooming,
    hasDental,
    hasDaycare,
    hasBoarding,
    hasEvaluation,
    hasTransportation,
    hasTraining,
    hasWalking
}: BookingTabVisibility): Array<BookingTab> => [
    {
        id: 'daycare',
        label: 'Daycare',
        path: readDaycareBookingsView() === 'list' ? BookingsDaycarePath : BookingsKanbanPath,
        icon: faTennisBall,
        visible: hasDaycare,
        isActive: (_, location) =>
            [BookingsDaycarePath, BookingsKanbanPath].some(path => location.pathname.startsWith(path))
    },
    {
        id: 'boarding',
        label: 'Boarding',
        path: BookingsBoardingPath,
        icon: faHotel,
        visible: hasBoarding,
        isActive: (_, location) => location.pathname.startsWith(BookingsBoardingPath)
    },
    {
        id: 'grooming',
        label: 'Grooming',
        path: BookingsGroomingPath,
        icon: faBath,
        visible: hasGrooming || hasMobileGrooming || hasDental,
        isActive: (_, location) =>
            ![
                BookingsDaycarePath,
                BookingsBoardingPath,
                BookingsEvaluationPath,
                BookingsTransportationPath,
                BookingsTrainingPath,
                BookingsWalkingPath,
                BookingsKanbanPath,
                BookingsAbandonedPath,
                BookingsRequestedPath,
                BookingsAllPath
            ].some(path => location.pathname.startsWith(path))
    },
    {
        id: 'training',
        label: 'Training',
        path: BookingsTrainingPath,
        icon: faWhistle,
        visible: hasTraining,
        isActive: (_, location) => location.pathname.startsWith(BookingsTrainingPath)
    },
    {
        id: 'transportation',
        label: 'Transportation',
        path: BookingsTransportationPath,
        icon: faVanShuttle,
        visible: hasTransportation,
        isActive: (_, location) => location.pathname.startsWith(BookingsTransportationPath)
    },
    {
        id: 'walking',
        label: 'Walking',
        path: BookingsWalkingPath,
        icon: faDogLeashed,
        visible: hasWalking,
        isActive: (_, location) => location.pathname.startsWith(BookingsWalkingPath)
    },
    {
        id: 'evaluation',
        label: 'Evaluation',
        path: BookingsEvaluationPath,
        icon: faListCheck,
        visible: hasEvaluation,
        isActive: (_, location) => location.pathname.startsWith(BookingsEvaluationPath)
    },
    {
        id: 'abandoned',
        label: 'Abandoned',
        path: BookingsAbandonedPath,
        icon: faCartXmark,
        visible: true
    },
    {
        id: 'requested',
        label: 'Requested',
        path: BookingsRequestedPath,
        icon: faHandWave,
        visible: true,
        isActive: (_, location) => location.pathname.startsWith(BookingsRequestedPath)
    },
    {
        id: 'all',
        label: 'All',
        path: BookingsAllPath,
        icon: faBorderAll,
        visible: true,
        isActive: (_, location) => location.pathname.startsWith(BookingsAllPath)
    }
];
